<script setup>
import { watch, computed, nextTick, shallowRef } from 'vue'
import { RouterView, useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { cloneDeep } from 'lodash-es'
import config from '@/config'
import { useConfigsStore } from '@/stores/configs'
import { useUserStore } from '@/stores/user'
import { useActivityStore } from '@/stores/activity'
import { useUiStore } from '@/stores/ui'
import { useConfirm } from '@/composables/ui'
import { useApi } from '@/composables/api'
import { i18n } from '@/i18n'

import Modalstack from '@/components/modalstack.vue'
import MessageStack from '@/components/messagestack.vue'
import BlockScreen from '@/components/blockscreen.vue'
import SignedLayout from '@/components/layouts/signed.vue'

import Login from '@/views/login.vue'
import SsoLogin from '@/views/login/sso.vue'
import Fillinfo from '@/views/login/fillinfo.vue'

const userStore = useUserStore()
const { isAuthorized, isValid, isLoggedIn, user } = storeToRefs(userStore)
const { returnUrl, isLoggingOut } = storeToRefs(useUiStore())

const router = useRouter()
const route = useRoute()
const isSsoLogin = computed(() => router.currentRoute.name === 'sso_login')
const query = shallowRef()

const { model: drafts, refresh: draftsFetch } = useApi({
  url: 'draft',
  before: () => {
    if (config.VITE_DRAFT_ENABLED == 'false') {
      return false
    }
    return true
  },
})
const { model: ssoResponse } = useApi({
  url: '/web/autologin',
  query,
  watch: [query],
  method: 'post',
})

watch(
  isLoggedIn,
  async isLoggedIn => {
    if (!isLoggedIn) {
      return
    }
    await Promise.all([useConfigsStore().fill(), useActivityStore().fetchActivityTypes()])
    await nextTick()
    await draftsFetch()
  },
  { immediate: true }
)

watch(
  isLoggedIn,
  async (isLoggedIn, isLoggedInPrev) => {
    if (isLoggedInPrev && !isLoggedIn) {
      await router.push({ name: 'login' })
      isLoggingOut.value = false
      return
    }

    if (
      !isLoggedInPrev &&
      isLoggedIn &&
      (['sso_login', 'login'].includes(router.currentRoute.value.name) ||
        (router.currentRoute.value.name === 'home' && returnUrl.value))
    ) {
      const rawNext = route.query.next
      const nextStr = Array.isArray(rawNext) ? rawNext[0] : rawNext
      const fromOAuth =
        typeof nextStr === 'string' && nextStr.startsWith('/') && !nextStr.startsWith('//')
          ? nextStr
          : null
      const targetRoute = fromOAuth ?? returnUrl.value ?? { name: 'home' }
      router.push(targetRoute)
      // Очищаем returnUrl только после успешного редиректа
      returnUrl.value = undefined
    }
  },
  { flush: 'post', immediate: false }
)

watch(drafts, async drafts => {
  if (!drafts) {
    return
  }

  if (router.currentRoute.value.fullPath == drafts.reflink) {
    return
  }

  if (!(await useConfirm(() => i18n.global.t('common.draftRestorePrompt'))())) {
    await useApi({
      url: `draft/${drafts.model_name}/${drafts.model_id}`,
      method: 'DELETE',
    }).refresh()
    return
  }

  router.replace(drafts.reflink)
})

watch(
  () => route.query,
  qry => {
    if (!qry.token || !qry.service) {
      return
    }

    query.value = { token: qry.token, service: qry.service }
  },
  { immediate: true }
)

watch(ssoResponse, resp => {
  if (!resp.access_token) {
    return
  }

  user.value = {
    ...cloneDeep(user.value),
    username: resp.username,
    access_token: resp.access_token,
  }
})
</script>

<template>
  <SsoLogin v-if="isSsoLogin" />
  <template v-else>
    <Login v-if="!isAuthorized" />
    <BlockScreen v-else-if="!isLoggedIn" />
    <template v-else>
      <SignedLayout v-if="isValid">
        <RouterView v-if="$route.name != 'login'" />
        <BlockScreen v-else />
      </SignedLayout>
      <Fillinfo v-else />
    </template>
  </template>

  <Teleport
    to="body"
    v-if="isAuthorized"
  >
    <Modalstack />
  </Teleport>
  <Teleport to="body">
    <MessageStack />
  </Teleport>
</template>
